// src/pages/billing/InvoiceEditPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useBillingInvoice, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

export const InvoiceEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useBillingInvoice();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    memo: '',
    due_date: '',
  });

  useEffect(() => {
    if (id) store.GetInvoice(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  useEffect(() => {
    if (store.currentItem) {
      const item = store.currentItem as any;
      setFormData({
        memo: item.memo ?? '',
        due_date: item.due_date ? new Date(item.due_date).toISOString().split('T')[0] : '',
      });
    }
  }, [store.currentItem]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    try {
      await store.UpdateInvoice(id, {
        memo: formData.memo,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : undefined,
      } as any);
      navigate(`/billing/invoices/${id}`);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/invoices" className="text-muted-foreground hover:text-foreground">Invoices</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.edit')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">Edit Invoice (Draft)</h1>
        <p className="text-sm text-muted-foreground mt-1">Only draft invoices can be edited. Memo and due date can be changed.</p>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
            <CardDescription>Update draft invoice information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Memo</label>
              <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px]" value={formData.memo} onChange={(e) => handleChange('memo', e.target.value)} placeholder="Invoice memo" rows={3} />
            </div>
            <Input label="Due Date" type="date" value={formData.due_date} onChange={(e) => handleChange('due_date', e.target.value)} />
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to={`/billing/invoices/${id}`}><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default InvoiceEditPage;
